import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";

import {
  selectCanSubmitRoofMaterialType,
  selectIsAddModalOpen,
  selectRoofMaterialTypeForm,
  selectRoofMaterialTypeSaving,
} from "../../redux/selector";
import { roofMaterialTypeActions } from "../../redux/slice";
import { FieldType, FieldValues, RoofMaterialTypeForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof RoofMaterialTypeForm;
  label: string;
};

const isNumberField = (name: keyof RoofMaterialTypeForm) =>
  FieldValues[name] === FieldType.Number;

const normalizeRoofMaterialTypeFieldValue = (
  name: keyof RoofMaterialTypeForm,
  value: string,
) => {
  if (isNumberField(name)) {
    return valueForOnlyNumericInputTextFields(value);
  }
  return valueForOnlyStringInputTextFields(value);
};

const FIELDS: FieldConfig[] = [
  { key: "materialTypeDesc", label: "DESCRIPTION" },
];

const ROOF_MATERIAL_TYPE_MODAL = "ROOF_MATERIAL_TYPE.MODAL";

const AddRoofMaterialModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectRoofMaterialTypeForm);
  const saving = useSelector(selectRoofMaterialTypeSaving);
  const canSubmit = useSelector(selectCanSubmitRoofMaterialType);

  const onClose = () => {
    if (saving) return;
    dispatch(roofMaterialTypeActions.closeAddModal());
  };

  const setField = (name: keyof RoofMaterialTypeForm, value: string) =>
    dispatch(
      roofMaterialTypeActions.setFormField({
        name,
        value: normalizeRoofMaterialTypeFieldValue(name, value),
      }),
    );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${ROOF_MATERIAL_TYPE_MODAL}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${ROOF_MATERIAL_TYPE_MODAL}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                variant="outlined"
                placeholder={t(
                  `${ROOF_MATERIAL_TYPE_MODAL}.PLACEHOLDERS.ENTER_${label}`,
                )}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                {...(isNumberField(key) ? { inputMode: "numeric" as const } : {})}
                onChange={(event) => setField(key, event.target.value)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!canSubmit || saving}
            onClick={() =>
              dispatch(roofMaterialTypeActions.addRoofMaterialType())
            }
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddRoofMaterialModal;
